import React, { Component } from 'react';
import { Grid, Row, Col, Form, FormGroup, ControlLabel } from 'react-bootstrap';
import Sprint from '../components/Sprint'
import Slider from '../components/Slider'
import MetricWidget from '../components/MetricWidget'
import HelpTooltip from '../components/HelpTooltip'

import qs from 'qs';

const DEFAULTS = {
    coverage: 0,
    jobVelocity: 40,
    includeSequences: false,
    sequenceVelocity: 1000,
    onshore: 2,
    offshore: 0,
    install: true,
    environments: 0,
    functional: 0,
    prodSupport: 0,
    postSupport: 0
};

const MINIMUMS = {
    coverage: 0,
    jobVelocity: 1,
    sequenceVelocity: 1,
    onshore: 1,
    offshore: 0,
    environments: 0,
    functional: 0,
    prodSupport: 0,
    postSupport: 0
};

const MAXIMUMS = {
    coverage: 100,
    jobVelocity: 100,
    sequenceVelocity: 1000,
    onshore: 4,
    offshore: 4,
    environments: 5,
    functional: 5,
    prodSupport: 10,
    postSupport: 5
};

class Estimate extends Component {

    calculateCoverage(coverage) {

        let data = JSON.parse(JSON.stringify(this.props.data));
        let totalComplexity = 0;
        let complexity = 0;
        let patterns = {};
        let jobs = 0;
        let sequences = 0;

        // Count sequences
        for (let assetName in data) {
            let asset = data[assetName];
            if (asset.assetType === "SEQUENCE_JOB") sequences++;
        }

        // If coverage is zero, we don't need to perform the calculations below
        // we still calculate sequences as that is not affected by coverage
        if (coverage === 0) {
            return {
                complexity: 0,
                jobs: 0,
                patterns: 0,
                sequences: sequences
            };
        }

        // Calculate total complexity
        for (let assetName in data) {
            if (!("complexityScore" in data[assetName]) || typeof data[assetName].complexityScore.total != "number") {
                delete data[assetName];
                continue;
            }

            totalComplexity += data[assetName].complexityScore.total;
        }

        // Calculate complexity, patters and jobs
        // NOTE: this is a separate loop to above because we need to know the total
        // complexity before doing this
        for (let assetName in data) {
            let asset = data[assetName];

            complexity += (asset.complexityScore.total / totalComplexity) * 100;

            let fingerprint = `${asset.complexityScore["Stage Type"]}:${asset.complexityScore["Cyclomatic"]}:${asset.complexityScore["Transformer LOC"]}`;
            patterns[fingerprint] = ++patterns[fingerprint] || 1;
            jobs++;

            if (coverage < complexity) break;
        }

        let uniquePatterns = Object.keys(patterns).length;

        return {
            complexity: complexity.toFixed(2),
            jobs: jobs,
            patterns: uniquePatterns,
            sequences: sequences
        }
    }

    handleParamChange = (param, value) => {
        let query = qs.parse(this.props.location.search.replace("?",""));

        query[param] = value;

        this.props.history.replace({
            pathname: this.props.history.location.pathname,
            search: `?${qs.stringify(query)}`
        });
    }

    // Methods to make sure estimate controls are the correct data type
    // and are in the acceptable range (will return the new value)

    validateInt(param, value) {
        if (!value || value > MAXIMUMS[param] || value < MINIMUMS[param]) {
            value = DEFAULTS[param];
        } else {
            value = parseInt(value);
        }

        return value;
    }

    validateBoolean(param, value) {
        if (value) {
            value = !(value.toLowerCase() === 'false'); // Defaults to true
        } else {
            value = DEFAULTS[param];
        }
        return value;
    }

    generateSprints(count, startIndex, name) {
        return [...Array(count || 0)].map((e, i) => (
            <Sprint key={startIndex} number={++startIndex} name={`${name} ${i+1}`}/>
        ));
    }

    renderSliderGroup(id, heading, value, unit=null) {
        return (
            <FormGroup key={ id } id={ `${id}FormGroup` } controlId={ id }>
                <ControlLabel>
                    { heading }
                    <HelpTooltip placement='right' heading={ heading } />
                </ControlLabel>
                <div style={{ padding: '10px 20px 30px 10px' }}>
                    <Slider
                        min={MINIMUMS[id]} max={MAXIMUMS[id]} suffix={unit} defaultValue={DEFAULTS[id]} value={value}
                        onChange={(value) => {this.handleParamChange(id, value)}}
                    />
                </div>
            </FormGroup>
        );
    }

    renderCheckboxGroup(id, heading, value) {
        return (
            <FormGroup key={ id }>
                <div className="checkbox" style={{ display: "inline-block" }}>
                    <label className="checkbox-custom check-success">
                        <input id={ id } name={ id } type="checkbox" checked={ value } onChange={(event) => {this.handleParamChange(id, event.target.checked)}}/>
                        <label htmlFor={ id }>{ heading }</label>
                        <HelpTooltip placement='right' heading={ heading } />
                    </label>
                </div>
            </FormGroup>
        );
    }

    render() {
        let values = qs.parse(this.props.location.search.replace("?",""));

        // Convert values from query string to correct types
        // and ensure all are in valid ranges
        for (let param in DEFAULTS) {
            switch (param) {
                case "install":
                case "includeSequences":
                    values[param] = this.validateBoolean(param, values[param]);
                    break;
                default:
                    values[param] = this.validateInt(param, values[param]);
                    break;
            }
        }

        let { complexity, jobs, patterns, sequences } = this.calculateCoverage(values.coverage);

        let iterations = (values.includeSequences) 
            ? Math.ceil(
                (patterns / (values.jobVelocity * (values.onshore + values.offshore))) +
                (sequences / (values.sequenceVelocity * (values.onshore + values.offshore)))
            )
            : Math.ceil((patterns / (values.jobVelocity * (values.onshore + values.offshore))))
        ;

        let sprints = [];
        sprints = sprints.concat(this.generateSprints((values.install) ? 1 : 0, sprints.length, "Install and Onboard"));
        sprints = sprints.concat(this.generateSprints(values.environments, sprints.length, "Target Environment Install"));
        sprints = sprints.concat(this.generateSprints(iterations, sprints.length, "Upgrade Iteration"));
        sprints = sprints.concat(this.generateSprints(values.functional, sprints.length, "Customer Functional Testing"));
        sprints = sprints.concat(this.generateSprints(values.prodSupport, sprints.length, "Production Go-live Support"));
        sprints = sprints.concat(this.generateSprints(values.postSupport, sprints.length, "Post Go-live Support"));

        return (
            <Grid className="wrapper">
                <Row>
                    <Col sm={5} md={4} lg={3} xl={2}>
                        <section className="panel">
                        <Form className="panel-body">

                            <div className="title widget">
                                <h1 className="pull-left">Customize Estimate</h1>
                            </div>

                            {[
                                this.renderSliderGroup("coverage", "Complexity Coverage", values["coverage"], "%"),
                                this.renderSliderGroup("jobVelocity", "Consultant Job Velocity", values["jobVelocity"])
                            ]}

                            {/* This one is rendered differently because it has a checkbox and slider */}
                            <FormGroup id="sequenceVelocityFormGroup" controlId="sequenceVelocity">
                                <ControlLabel>
                                    <div className="checkbox" style={{ display: "inline-block" }}>
                                        <label className="checkbox-custom check-success">
                                            <input id="includeSequences" name="includeSequences" type="checkbox" checked={values.includeSequences} onChange={(event) => {this.handleParamChange("includeSequences", event.target.checked)}}/>
                                            <label htmlFor="includeSequences">Consultant Sequence Velocity</label>
                                            <HelpTooltip placement='right' heading="Consultant Sequence Velocity" />
                                        </label>
                                    </div>
                                </ControlLabel>
                                { values.includeSequences && 
                                <div style={{ padding: '10px 20px 30px 10px' }}>
                                    <Slider
                                        min={MINIMUMS.sequenceVelocity} max={MAXIMUMS.sequenceVelocity} defaultValue={DEFAULTS.sequenceVelocity} value={values.sequenceVelocity}
                                        onChange={(value) => {this.handleParamChange("sequenceVelocity", value)}}
                                    />
                                </div> }
                            </FormGroup>

                            {[
                                this.renderSliderGroup("onshore", "On-shore Consultants", values["onshore"]),
                                this.renderSliderGroup("offshore", "Off-shore Consultants", values["offshore"])
                            ]}

                            <div className="title">
                                <h1 className="pull-left">Optional Addons</h1>
                                <HelpTooltip placement='right' heading="Optional Addons" />
                            </div>

                            {[
                                this.renderSliderGroup("environments", "Target Environment Installs", values["environments"]),
                                this.renderSliderGroup("functional", "Customer Functional Testing Sprints", values["functional"]),
                                this.renderSliderGroup("prodSupport", "Production Go-live Support Sprints", values["prodSupport"]),
                                this.renderSliderGroup("postSupport", "Post Go-live Support Sprints", values["postSupport"]),
                                this.renderCheckboxGroup("install", "Install MettleCI", values["install"])
                            ]}

                        </Form>
                        </section>
                    </Col>
                    <Col sm={7} md={8} lg={9} xl={10}>
                        <Row>
                            <Col xs={6} lg={3}>
                                <MetricWidget alt colour="yellow" icon="fa-cogs" name="Project Complexity Tested" title="Project Complexity Tested" value={`${complexity}%`} />
                            </Col>
                            <Col xs={6} lg={3}>
                                <MetricWidget alt colour="purple" icon="fa-bug" name="Unique Job Patterns" title={<span>Unique Job patterns across <strong>{jobs}</strong> jobs</span>} value={patterns} />
                            </Col>
                            <Col xs={6} lg={3}>
                                <MetricWidget alt colour="blue" icon="fa-user" name="Consultants" title="Consultants" value={`${values.onshore + values.offshore}`} />
                            </Col>
                            <Col xs={6} lg={3}>
                                <MetricWidget alt colour="green" icon="fa-clock-o" name="Months' Subscription" title="Months' Subscription" value={`${Math.ceil(sprints.length/4)}`} />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12}>
                                <section className="panel">

                                    <header className="panel-heading head-border">
                                        Sprints (1 week each)
                                        <HelpTooltip placement='right' heading="Sprint List" />
                                    </header>

                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Sprint Name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sprints}
                                            <tr><td></td><td>...</td></tr>
                                        </tbody>
                                    </table>
                                </section>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Grid>
        );
    }
}

export default Estimate;
